import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  TextField,
  Box,
  IconButton,
  Tooltip,
  Chip,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Collapse,
  Typography
} from '@mui/material';
import {
  Search,
  FilterList,
  GetApp,
  VisibilityOff,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const EnhancedTable = ({
  data = [],
  columns = [],
  title,
  searchable = true,
  sortable = true,
  paginated = true,
  exportable = false,
  onRowClick,
  loading = false,
  emptyMessage = "No data available",
  initialRowsPerPage = 10,
  hideRowsPerPageOptions = false, 
  stickyHeader = true,
  dense = false,
  showRowNumbers = false,
  collapsibleRows = false,
  renderCollapsedContent,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Sorting function
  const handleSort = (columnId) => {
    if (!sortable) return;
    
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = data;

    // Apply search filter
    if (searchTerm && searchable) {
      filtered = data.filter(row =>
        columns.some(column => {
          const value = row[column.id];
          return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply sorting
    if (orderBy && sortable) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];
        
        if (aValue < bValue) return order === 'asc' ? -1 : 1;
        if (aValue > bValue) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, searchTerm, orderBy, order, columns, searchable, sortable]);

  // Pagination
  const paginatedData = paginated && processedData.length > 0
    ? processedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : processedData;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRowExpansion = (rowId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const visibleColumnsArray = columns.filter(col => !col.hidden);

  return (
    <Box>
      {/* Table Controls */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexWrap: 'wrap',
        gap: 2
      }}>
        {searchable && (
          <TextField
            size="small"
            fullWidth
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          {exportable && (
            <Tooltip title="Export Data">
              <IconButton size="small">
                <GetApp fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: "0.8rem" }} color="text.secondary">
          Showing {paginatedData.length} of {processedData.length} results
          {searchTerm && ` for "${searchTerm}"`}
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          maxHeight: isMobile ? '70vh' : '80vh'
        }}
      >
        <Table 
          stickyHeader={stickyHeader} 
          size={dense ? 'small' : 'medium'}
          sx={{ minWidth: 650 }}
        >
          <TableHead>
            <TableRow>
              {showRowNumbers && (
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.secondary' }}>
                  #
                </TableCell>
              )}
              {collapsibleRows && (
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.secondary', width: 50 }}>
                </TableCell>
              )}
              {visibleColumnsArray.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ 
                    fontWeight: 600, 
                    backgroundColor: 'background.secondary',
                    minWidth: column.minWidth || 'auto'
                  }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {sortable && column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <AnimatePresence>
              {Array.isArray(paginatedData) && paginatedData.map((row, index) => {
                const rowId = row.id || index;
                const isExpanded = expandedRows.has(rowId);
                
                return (
                  <React.Fragment key={rowId}>
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      component={TableRow}
                      onClick={() => onRowClick && onRowClick(row)}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider', 
                        cursor: onRowClick ? 'pointer' : 'default',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      {showRowNumbers && (
                        <TableCell sx={{ fontSize: '0.8rem', 
                          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                          '&:last-child td': { borderBottom: 0 }
                        }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                      )}
                      {collapsibleRows && (
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowExpansion(rowId);
                            }}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                      )}
                      {visibleColumnsArray.map((column) => (
                        <TableCell key={column.id} sx={{ fontSize: '0.8rem', 
                          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                          '&:last-child td': { borderBottom: 0 } }}>
                          {column.render ? column.render(row[column.id], row) : row[column.id]}
                        </TableCell>
                      ))}
                    </motion.tr>
                    
                    {collapsibleRows && (
                      <TableRow>
                        <TableCell 
                          style={{ paddingBottom: 0, paddingTop: 0 }} 
                          colSpan={visibleColumnsArray.length + (showRowNumbers ? 1 : 0) + 1}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              {renderCollapsedContent && renderCollapsedContent(row)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </AnimatePresence>
          </TableBody>
        </Table>

        {paginatedData.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* Pagination */}
      {paginated && processedData.length > 0 && (
        <TablePagination
          rowsPerPageOptions={hideRowsPerPageOptions ? [] : [5, 10, 25, 50]}
          component="div"
          count={processedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      )}
    </Box>
  );
};

export default EnhancedTable;